import { useEffect, useRef, useState } from 'react';

import { Navigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminOrganisationLayout } from './admin-organisation-layout';
import { SwitchPricingModelModal } from './switch-pricing-model-modal';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import {
  ORG_PERMISSION_CONFIGS,
  ORG_PERMISSION_SECTIONS,
  OrgPermission,
  OrgPermissionConfig,
} from '../../../../../../../backend/src/organisation/organisation-permission';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useSetOrganisationPermission } from '@/api/mutations/useSetOrganisationPermission';
import { useUpdateOrganisationBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { useUpdateOrganisationBlocking } from '@/api/mutations/useUpdateOrganisationBlocking';
import { useUpdateOrganisationIsTrialPackage } from '@/api/mutations/useUpdateOrganisationIsTrialPackage';
import { useUpdateSlackChannel } from '@/api/mutations/useUpdateSlackChannel';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { dateToDatetimeLocal } from '@/helpers/formatDate';
import { useHasPermission } from '@/hooks/useHasPermission';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsRow,
  SettingsSection,
  SettingsSectionFooter,
  SettingsSectionHeading,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Switch } from '@/new/components/shadcn/switch';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { BillingPlanHistory } from '@/new/pages/admin/organisations/_organisationId/billing-plan-history';
import { CreditLimits } from '@/new/pages/organisation-settings/components/credit-limits';

const isUsingOnboardingPackage = {
  id: 'onboarding-package-toggle',
  title: 'Onboarding Package',
  description:
    'Whether this organisation is currently on an onboarding package with increased usage limits. This will be indicated in the usage dashboard (and otherwise has no other effects).',
};

function PermissionRow({
  permission,
  isEnabled,
  onCheckedChange,
  disabled,
  isLoading = false,
}: {
  permission: Pick<OrgPermissionConfig, 'id' | 'title' | 'description'>;
  isEnabled: boolean;
  onCheckedChange: (enabled: boolean) => void;
  disabled?: boolean;
  isLoading?: boolean;
}) {
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  return (
    <SettingsRow title={permission.title} description={permission.description}>
      <Switch
        checked={isEnabled}
        disabled={disabled || isLoading}
        id={permission.id}
        onCheckedChange={(enabled) => setPendingEnabled(enabled)}
      />

      <ConfirmationModal
        isOpen={pendingEnabled !== null}
        onOpenChange={(open) => {
          if (!open) setPendingEnabled(null);
        }}
        title={pendingEnabled ? 'Enable permission' : 'Disable permission'}
        onConfirm={() => {
          if (pendingEnabled === null) return;
          onCheckedChange(pendingEnabled);
          setPendingEnabled(null);
        }}
        isLoading={isLoading}
      >
        <p>
          Are you sure you want to {pendingEnabled ? 'enable' : 'disable'} the{' '}
          <span className="font-semibold">{permission.title}</span> permission for this organisation?
        </p>
      </ConfirmationModal>
    </SettingsRow>
  );
}

export function AdminManageOrganisation({
  section = 'configuration',
}: {
  section?: 'configuration' | 'billing' | 'general';
}) {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  usePlatformPermissionCheck([Permission.PlatformManageOrganisations, Permission.PlatformManageBilling]);
  const { hasPermission } = useHasPermission();
  const canManageOrganisation = hasPermission(Permission.PlatformManageOrganisations);
  const canManageBilling = hasPermission(Permission.PlatformManageBilling);
  const canManageUsers = hasPermission(Permission.PlatformManageUsers);

  const { data: organisation } = useGetOrganisation(organisationId);

  const { mutate: updateBlocking } = useUpdateOrganisationBlocking(organisationId);
  const { mutate: setOrganisationPermission, isLoading: isSettingPermission } =
    useSetOrganisationPermission(organisationId);
  const { mutate: updateBillingPeriod, isLoading: isUpdatingBillingPeriod } =
    useUpdateOrganisationBillingPeriod(organisationId);
  const { mutate: updateIsTrialPackage } = useUpdateOrganisationIsTrialPackage(organisationId);
  const { mutate: updateSlackChannel, isLoading: isUpdatingSlackChannel } = useUpdateSlackChannel(organisationId);

  const [maxCredits, setMaxCredits] = useState<number | undefined>();
  const [newMaxCredits, setNewMaxCredits] = useState<number | undefined>();
  const [newMaxCreditsChanged, setNewMaxCreditsChanged] = useState(false);
  const newMaxCreditsOrganisationId = useRef<string>();
  const [seatCount, setSeatCount] = useState<number | undefined>();
  const [billingStartDate, setBillingStartDate] = useState<Date | undefined>();
  const [billingEndDate, setBillingEndDate] = useState<Date | undefined>();
  const [slackChannelId, setSlackChannelId] = useState<string>('');
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [defaultCreditLimit, setDefaultCreditLimit] = useState<number | undefined>();

  const isSeatBased = organisation?.pricingModel === PricingModel.SeatBased;
  const limitsChanged = organisation
    ? isSeatBased
      ? seatCount !== (organisation.seatCount ?? undefined) ||
        defaultCreditLimit !== (organisation.defaultCreditLimit ?? undefined)
      : maxCredits !== (organisation.maxCredits ?? undefined)
    : false;
  const billingDatesChanged = organisation
    ? billingStartDate?.getTime() !==
        (organisation.billingStartDate ? new Date(organisation.billingStartDate).getTime() : undefined) ||
      billingEndDate?.getTime() !==
        (organisation.billingEndDate ? new Date(organisation.billingEndDate).getTime() : undefined)
    : false;

  function setPermission(permission: OrgPermission, enabled: boolean) {
    if (!organisation) return;

    setOrganisationPermission({ permission, enabled });
  }

  useEffect(() => {
    setMaxCredits(organisation?.maxCredits ?? undefined);
    setSeatCount(organisation?.seatCount ?? undefined);
    setBillingStartDate(organisation?.billingStartDate ? new Date(organisation.billingStartDate) : undefined);
    setBillingEndDate(organisation?.billingEndDate ? new Date(organisation?.billingEndDate) : undefined);
    setSlackChannelId(organisation?.slackChannelId ?? '');
    setDefaultCreditLimit(organisation?.defaultCreditLimit ?? undefined);
  }, [organisation]);

  useEffect(() => {
    const organisationMaxCredits = organisation?.maxCredits ?? undefined;
    const organisationChanged = newMaxCreditsOrganisationId.current !== organisation?.id;
    const prospectiveLimitWasSaved = organisationMaxCredits === newMaxCredits;

    if (organisationChanged || !newMaxCreditsChanged || prospectiveLimitWasSaved) {
      newMaxCreditsOrganisationId.current = organisation?.id;
      setNewMaxCredits(organisationMaxCredits);
      setNewMaxCreditsChanged(false);
    }
  }, [organisation, newMaxCredits, newMaxCreditsChanged]);

  function validateLimits() {
    if (isSeatBased) {
      if (seatCount === undefined) {
        toast.error(`Seat count can't be empty`);
        return false;
      }
      if (defaultCreditLimit === undefined || defaultCreditLimit < 1) {
        toast.error(`Default credit limit can't be empty`);
        return false;
      }
    } else if (maxCredits === undefined) {
      toast.error(`Maximum credits can't be empty`);
      return false;
    }

    return true;
  }

  function handleLimitsSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateLimits()) return;
    if (!organisation?.billingStartDate || !organisation.billingEndDate) {
      toast.error('Configure the billing period dates before updating its limits');
      return;
    }

    if (isSeatBased) {
      updateBillingPeriod({
        seatCount: seatCount!,
        defaultCreditLimit: defaultCreditLimit!,
        billingStartDate: new Date(organisation.billingStartDate),
        billingEndDate: new Date(organisation.billingEndDate),
      });
    } else {
      updateBillingPeriod({
        maxCredits: maxCredits!,
        billingStartDate: new Date(organisation.billingStartDate),
        billingEndDate: new Date(organisation.billingEndDate),
      });
    }
  }

  function handleBillingPeriodSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!billingStartDate || !billingEndDate) {
      toast.error(`Billing period dates can't be empty`);
      return;
    }

    if (!isSeatBased && newMaxCredits === undefined) {
      toast.error(`Maximum credits can't be empty`);
      return;
    }

    updateBillingPeriod({
      ...(!isSeatBased && { maxCredits: newMaxCredits }),
      billingStartDate,
      billingEndDate,
    });
  }

  if (section === 'configuration' && !canManageOrganisation && canManageBilling) {
    return <Navigate to={`/admin/organisations/${organisationId}/billing`} replace />;
  }

  return (
    <AdminOrganisationLayout>
      {organisation && (
        <>
          {section === 'configuration' && canManageOrganisation && (
            <SettingsContent>
              {ORG_PERMISSION_SECTIONS.map((permissionSection) => {
                const configs = ORG_PERMISSION_CONFIGS.filter((config) => config.kind === permissionSection.kind);
                return (
                  <SettingsSection
                    key={permissionSection.kind}
                    title={permissionSection.title}
                    description={permissionSection.description}
                  >
                    {configs.length === 0 ? (
                      <SettingsRow title={permissionSection.emptyText} />
                    ) : (
                      configs.map((permission) => (
                        <PermissionRow
                          key={permission.id}
                          permission={permission}
                          isEnabled={organisation.permissions.includes(permission.permission)}
                          onCheckedChange={(enabled) => setPermission(permission.permission, enabled)}
                          isLoading={isSettingPermission}
                        />
                      ))
                    )}
                  </SettingsSection>
                );
              })}
            </SettingsContent>
          )}

          {section === 'billing' && canManageBilling && (
            <SettingsContent>
              <SwitchPricingModelModal
                isOpen={isPricingModalOpen}
                onOpenChange={setIsPricingModalOpen}
                organisation={organisation}
              />

              <SettingsSection title="Billing" description="Configure the organisation's billing model and limits.">
                <SettingsRow
                  title="Pricing model"
                  description={
                    <>
                      Currently set to{' '}
                      <span className="font-medium text-gray-700">
                        {organisation.pricingModel === PricingModel.SeatBased ? 'seat-based' : 'usage-based'}
                      </span>
                      .
                      {isSeatBased ? (
                        <>
                          {' '}
                          Switching to usage-based lets you set the organisation's maximum credit allowance.
                          {canManageUsers && (
                            <>
                              {' '}
                              Per-user limits can be managed from the{' '}
                              <a
                                href={`/admin/organisations/${organisation.id}/users`}
                                className="font-medium text-gray-700 underline hover:text-gray-900"
                              >
                                Users
                              </a>{' '}
                              tab.
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {' '}
                          Switching to seat-based lets you set the number of seats and default per-user credit limit.
                        </>
                      )}
                    </>
                  }
                >
                  <Button onClick={() => setIsPricingModalOpen(true)}>
                    <Icon className="lucide--arrow-left-right" />
                    {isSeatBased ? 'Switch to usage-based' : 'Switch to seat-based'}
                  </Button>
                </SettingsRow>
                <PermissionRow
                  permission={isUsingOnboardingPackage}
                  isEnabled={organisation.isUsingTrialPackage}
                  onCheckedChange={() =>
                    updateIsTrialPackage({ isUsingTrialPackage: !organisation.isUsingTrialPackage })
                  }
                />
                <PermissionRow
                  permission={{
                    id: 'blocking-toggle',
                    title: 'Blocking Enabled',
                    description:
                      'When enabled, users/organisations who exceed their usage limits will be blocked from further uploads and runs.',
                  }}
                  isEnabled={organisation.isBlockingEnabled}
                  onCheckedChange={() =>
                    updateBlocking(
                      {
                        isBlockingEnabled: !organisation.isBlockingEnabled,
                      },
                      {
                        onSuccess: () => {
                          toast.success(
                            `Blocking ${organisation.isBlockingEnabled ? 'disabled' : 'enabled'} for this organisation`,
                          );
                        },
                      },
                    )
                  }
                />
              </SettingsSection>

              <CreditLimits
                organisation={organisation}
                organisationId={organisationId}
                canManage
                includeContentWrapper={false}
                showReviewerInstructions={false}
              />

              <section className="space-y-8">
                <SettingsSectionHeading
                  title="Billing plan"
                  description="Update the current plan's limits in place, or change the billing dates to close its effective range and write a new current plan. Previous plans remain in the history below."
                />

                <div className="space-y-4">
                  <form onSubmit={handleLimitsSubmit}>
                    <SettingsSection
                      footer={
                        <SettingsSectionFooter className="justify-end">
                          <Button type="submit" disabled={isUpdatingBillingPeriod || !limitsChanged}>
                            {isSeatBased ? 'Update seat limits' : 'Update maximum credits'}
                          </Button>
                        </SettingsSectionFooter>
                      }
                    >
                      {isSeatBased ? (
                        <>
                          <SettingsRow title="Number of seats">
                            <Input
                              className="w-48"
                              type="number"
                              id="seat-count"
                              value={seatCount ?? ''}
                              onChange={(e) => setSeatCount(Number(e.target.value))}
                              disabled={isUpdatingBillingPeriod}
                              min="1"
                            />
                          </SettingsRow>
                          <SettingsRow
                            title={
                              <span className="flex items-center gap-1.5">
                                Default credit limit
                                <TooltipIcon
                                  tooltipContent="Applied to every current user in the org (resetting per-user overrides) and used as the starting limit for any new user added later."
                                  triggerClassName="h-3.5 w-3.5"
                                />
                              </span>
                            }
                          >
                            <Input
                              className="w-48"
                              type="number"
                              id="default-credit-limit"
                              value={defaultCreditLimit ?? ''}
                              onChange={(e) => setDefaultCreditLimit(Number(e.target.value))}
                              disabled={isUpdatingBillingPeriod}
                              min="1"
                            />
                          </SettingsRow>
                        </>
                      ) : (
                        <SettingsRow
                          title="Current maximum credits"
                          description="The credit allowance on the current billing plan."
                        >
                          <Input
                            className="w-48"
                            type="number"
                            id="max-credits"
                            value={maxCredits ?? ''}
                            onChange={(e) => setMaxCredits(Number(e.target.value))}
                            disabled={isUpdatingBillingPeriod}
                          />
                        </SettingsRow>
                      )}
                    </SettingsSection>
                  </form>

                  <form onSubmit={handleBillingPeriodSubmit}>
                    <SettingsSection
                      footer={
                        <SettingsSectionFooter className="justify-end">
                          <Button type="submit" disabled={isUpdatingBillingPeriod || !billingDatesChanged}>
                            {isSeatBased
                              ? 'Write new billing period'
                              : `Write new period with ${newMaxCredits?.toLocaleString('en-GB') ?? '…'} maximum credits`}
                          </Button>
                        </SettingsSectionFooter>
                      }
                    >
                      <SettingsRow title="Billing period start date">
                        <Input
                          className="w-60"
                          type="datetime-local"
                          id="start-date"
                          value={billingStartDate ? dateToDatetimeLocal(billingStartDate) : ''}
                          onChange={(e) => setBillingStartDate(new Date(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                        />
                      </SettingsRow>

                      <SettingsRow title="Billing period end date">
                        <Input
                          className="w-60"
                          type="datetime-local"
                          id="end-date"
                          value={billingEndDate ? dateToDatetimeLocal(billingEndDate) : ''}
                          onChange={(e) => setBillingEndDate(new Date(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                        />
                      </SettingsRow>

                      {!isSeatBased && (
                        <SettingsRow
                          title="Maximum credits"
                          description="The credit allowance for the new billing plan."
                        >
                          <Input
                            className="w-48"
                            type="number"
                            id="new-max-credits"
                            value={newMaxCredits ?? ''}
                            onChange={(e) => {
                              setNewMaxCredits(Number(e.target.value));
                              setNewMaxCreditsChanged(true);
                            }}
                            disabled={isUpdatingBillingPeriod}
                          />
                        </SettingsRow>
                      )}
                    </SettingsSection>
                  </form>
                </div>
              </section>

              <BillingPlanHistory organisationId={organisationId} />
            </SettingsContent>
          )}

          {section === 'general' && canManageOrganisation && (
            <SettingsContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateSlackChannel(
                    { slackChannelId: slackChannelId.trim() },
                    {
                      onSuccess: () => {
                        toast.success('Slack channel updated');
                      },
                    },
                  );
                }}
              >
                <SettingsSection
                  title="General"
                  footer={
                    <SettingsSectionFooter className="justify-end">
                      <Button
                        type="submit"
                        disabled={isUpdatingSlackChannel || slackChannelId === (organisation?.slackChannelId ?? '')}
                      >
                        Update Slack Channel
                      </Button>
                    </SettingsSectionFooter>
                  }
                >
                  <SettingsRow
                    title="Wexler Slack Channel ID"
                    description="Organisation-specific run alerts are sent here. Leave empty to disable these alerts; generic Run Added messages still go to the monitoring channel."
                  >
                    <Input
                      className="w-64"
                      type="text"
                      id="slack-channel-id"
                      placeholder="e.g. C0123456789"
                      value={slackChannelId}
                      onChange={(e) => setSlackChannelId(e.target.value)}
                      disabled={isUpdatingSlackChannel}
                    />
                  </SettingsRow>
                </SettingsSection>
              </form>
            </SettingsContent>
          )}
        </>
      )}
    </AdminOrganisationLayout>
  );
}
